import { useMemo } from 'react';
import { useQueryClient } from 'react-query';

import { impersonateUser } from 'apis/UserAPI';
import {
  canAccessActionPlans,
  canAccessActionPlansCompanyView,
  canAccessAICompetitiveIntelligence,
  canAccessAISpeedToLead,
  canAccessComingSoonFeatures,
  canViewPricingData,
  hasCompanyAIOverviewFlag,
  hasPermissionOrAdmin,
  isIncentivesCorporate,
  isIncentivesSalesperson,
  isMysteryShopAdmin,
  isMysteryShopShopper,
} from 'helpers/permissions';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { handleLogin, UserType } from 'store/slices/authSlice';
import { handleReset } from 'store/slices/globalSlice';

import { useRootContext } from './RootContext';
import { COMPANY_COMMUNITY_RELATION_TYPE } from '../../constants/company';
import { VIEW_PERMISSIONS } from '../../permissions/views';

let queuedSupportModeRedirectPath: string | null = null;

export const getUserSwitchRedirectPath = (userInfo: UserType | null) => {
  if (!userInfo) {
    return '/';
  }

  if (userInfo.isSuperuser) {
    return '/companies';
  }

  if (isMysteryShopAdmin(userInfo) || isMysteryShopShopper(userInfo)) {
    return '/mystery-shopping';
  }

  // The AI Company Overview landing decision needs the community list, which PrivateRoot waits for.
  if (hasCompanyAIOverviewFlag(userInfo)) {
    return '/';
  }

  const showPricingData = canViewPricingData(userInfo);
  const canUseAICompetitiveIntelligence = canAccessAICompetitiveIntelligence(userInfo);
  const canUseSpeedToLead = canAccessAISpeedToLead(userInfo);

  if (!showPricingData && (canUseAICompetitiveIntelligence || canUseSpeedToLead)) {
    return '/ai-competitive-intelligence';
  }

  return '/overview';
};

export const queueSupportModeRedirectPath = (path: string) => {
  queuedSupportModeRedirectPath = path;
};

export const consumeSupportModeRedirectPath = () => {
  const path = queuedSupportModeRedirectPath;
  queuedSupportModeRedirectPath = null;
  return path;
};

export const useUserSwitch = () => {
  const { showMessage } = useRootContext();
  const { currentUser } = useAppSelector((state) => state.auth);
  const queryClient = useQueryClient();

  const dispatch = useAppDispatch();

  const handleUserSwitch = async (userId: number, onSuccessRedirect?: (userInfo: UserType | null) => void) => {
    await impersonateUser({
      user_id: currentUser?.manager ? undefined : userId,
    })
      .then((response: any) => {
        const payload = {
          // The backend sets current_view (supported user view when support mode starts)
          currentUser: response?.userInfo,
          accessToken: response?.accessToken,
          refreshToken: response?.refreshToken,
        };

        setTimeout(async () => {
          if (response?.userInfo && Boolean(response?.userInfo?.manager)) {
            showMessage('success', `Supporting user ${response?.userInfo?.FullName ?? response?.userInfo?.name}.`);
          }

          await Promise.all([
            queryClient.cancelQueries(['global', 'company', 'list']),
            queryClient.cancelQueries(['user-communities']),
          ]);
          queryClient.removeQueries(['global', 'company', 'list']);
          queryClient.removeQueries(['user-communities']);

          dispatch(handleReset());
          dispatch(handleLogin(payload));

          if (onSuccessRedirect) {
            onSuccessRedirect(payload.currentUser ?? null);
          }
        }, 500);
      })
      .catch((reason: any) => {
        showMessage('error', `Trouble initiating support mode!`);
      });
  };

  return { handleUserSwitch };
};

export const useSidebarMenuLogic = () => {
  const { selectedCompany, selectedCommunity, communities } = useAppSelector((state) => state.global);
  const { currentUser } = useAppSelector((state) => state.auth);

  // This `showOverview` is used only in the Company selected scenario. When a Community is selected, the component
  // `SidebarMenuDefaultCommunityViews` takes over and handles permissions.
  const showOverview = useMemo(() => {
    return (
      Boolean(currentUser) &&
      !selectedCommunity &&
      (selectedCompany || Boolean(currentUser?.holding)) &&
      canViewPricingData(currentUser)
    );
  }, [currentUser, selectedCompany, selectedCommunity]);

  const showProposals = useMemo(() => {
    return (
      currentUser?.feature_flags?.view_pricing_data &&
      Boolean(currentUser && !currentUser.isSuperuser) &&
      (isIncentivesCorporate(currentUser) || isIncentivesSalesperson(currentUser))
    );
  }, [currentUser]);

  const showShopRequests = useMemo(() => {
    return Boolean(currentUser && (isMysteryShopAdmin(currentUser) || isMysteryShopShopper(currentUser)));
  }, [currentUser]);

  const showShopperContent = useMemo(() => {
    return Boolean(
      currentUser && isMysteryShopShopper(currentUser) && !currentUser.isSuperuser && !isMysteryShopAdmin(currentUser)
    );
  }, [currentUser]);

  const showOtherMenu = useMemo(() => {
    return currentUser?.feature_flags?.show_beta_settings || (!!selectedCommunity && !showShopperContent);
  }, [currentUser, showProposals, selectedCommunity, showShopperContent]);

  const showAIRecommendations = useMemo(() => {
    return (
      currentUser?.feature_flags?.view_pricing_data &&
      currentUser?.feature_flags?.show_ai_recommendations &&
      !currentUser?.isSuperuser &&
      (Boolean(selectedCompany) ||
        Boolean(currentUser?.holding) ||
        selectedCommunity?.type == COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY)
    );
  }, [currentUser, selectedCommunity]);

  const showAICompetitiveIntelligence = useMemo(() => {
    if (!canAccessAICompetitiveIntelligence(currentUser) || currentUser?.isSuperuser) {
      return false;
    }

    // Community context
    if (selectedCommunity?.type == COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY) {
      return true;
    }

    // Company context is shown only for users who have access to more than one community
    const inCompanyContext = Boolean(selectedCompany) || Boolean(currentUser?.holding);
    return inCompanyContext && communities.length > 1;
  }, [currentUser, selectedCompany, selectedCommunity, communities]);

  const showAISpeedToLead = useMemo(() => {
    if (!canAccessAISpeedToLead(currentUser) || currentUser?.isSuperuser) {
      return false;
    }

    if (selectedCommunity?.type == COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY) {
      return true;
    }

    const inCompanyContext = Boolean(selectedCompany) || Boolean(currentUser?.holding);
    return inCompanyContext && communities.length > 1;
  }, [currentUser, selectedCompany, selectedCommunity, communities]);

  const showComingSoonFeatures = useMemo(() => {
    return canAccessComingSoonFeatures(currentUser) && !currentUser?.isSuperuser;
  }, [currentUser]);

  const showCommunitiesToWatch = useMemo(() => {
    return (
      Boolean(currentUser?.feature_flags?.show_cmi_digest) &&
      Boolean(currentUser?.cmi_digest_preference?.enabled) &&
      !currentUser?.isSuperuser
    );
  }, [currentUser]);

  const showSalesTalkTracks = useMemo(() => {
    return (
      currentUser?.feature_flags?.show_sales_talk_tracks &&
      !currentUser?.isSuperuser &&
      selectedCommunity?.type == COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY
    );
  }, [currentUser, selectedCommunity]);

  const showActionPlans = useMemo(() => {
    return (
      canAccessActionPlans(currentUser) &&
      !currentUser?.isSuperuser &&
      selectedCommunity?.type == COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY
    );
  }, [currentUser, selectedCommunity]);

  const showActionPlansCompanyView = useMemo(() => {
    if (!canAccessActionPlansCompanyView(currentUser)) {
      return false;
    }

    if (selectedCommunity?.type == COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY) {
      return false;
    }

    const inCompanyContext = Boolean(selectedCompany) || Boolean(currentUser?.holding);
    return inCompanyContext && communities.length > 1;
  }, [currentUser, selectedCompany, selectedCommunity, communities]);

  const showAlerts = useMemo(() => {
    return (
      currentUser?.feature_flags?.show_alerts &&
      !currentUser?.isSuperuser &&
      selectedCommunity?.type == COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY
    );
  }, [currentUser, selectedCommunity]);

  const showMarketingCampaigns = useMemo(() => {
    return (
      currentUser?.feature_flags?.show_marketing_campaigns &&
      !currentUser?.isSuperuser &&
      selectedCommunity?.type == COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY
    );
  }, [currentUser, selectedCommunity]);

  const showCustomViews = useMemo(() => {
    return selectedCommunity && hasPermissionOrAdmin(currentUser, [VIEW_PERMISSIONS.CUSTOM.VIEW]);
  }, [currentUser, selectedCommunity]);

  const showPricingData = useMemo(() => {
    return canViewPricingData(currentUser);
  }, [currentUser]);

  return {
    showOverview,
    showCustomViews,
    showProposals,
    showShopRequests,
    showShopperContent,
    showOtherMenu,
    showAIRecommendations,
    showAICompetitiveIntelligence,
    showAISpeedToLead,
    showComingSoonFeatures,
    showCommunitiesToWatch,
    showSalesTalkTracks,
    showActionPlans,
    showActionPlansCompanyView,
    showAlerts,
    showPricingData,
    showMarketingCampaigns,
  };
};
